import Captcha from "@/common/components/Captcha";
import GoogleSignInButton from "@/common/components/GoogleSignInButton";
import SEO from "@/common/components/seo";
import LogoIcon from "@/common/svgs/logo";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { HomeRoute } from "@/features/home/route";
import { Link } from "react-router-dom";
import ActivateAccountDialog from "../containers/activate-account-dialog";
import ForgotPasswordDialog from "../containers/forgot-password-dialog";
import ResetPasswordDialog from "../containers/reset-password-dialog";
import useLogin from "../hooks/use-login";
import { SignUpRoute } from "../route";

export default function LoginPage() {
	const {
		onSubmit,
		form,
		isSubmitting,

		onResendVerificationCode,
		activateAccountForm,
		onActivateAccountSubmit,
		showActivateAccountDialog,
		setShowActivateAccountDialog,

		forgotPasswordForm,
		onForgotPasswordSubmit,
		showForgotPasswordDialog,
		setShowForgotPasswordDialog,

		resetPasswordForm,
		onResetPasswordSubmit,
		showResetPasswordDialog,
		setShowResetPasswordDialog,

		captchaToken,
		setCaptchaToken,

		onGoogleCredential,
	} = useLogin();

	return (
		<div
			className="flex h-full min-h-screen w-full items-center justify-center px-4 py-10"
			style={{
				background:
					"radial-gradient(196.52% 110.66% at 52.19% 30.42%, rgba(46, 219, 202, 0.30) 0%, rgba(8, 50, 46, 0.00) 100%), radial-gradient(321.2% 194.09% at 77.63% -11.3%, #2EDBCA 4.81%, #004096 39.75%, #470096 100%)",
				backgroundBlendMode: "plus-lighter, normal",
			}}
		>
			<SEO
				title="Log In - Reel Rewards"
				description="Log in to Reel Rewards to manage your profile, membership, entries, and rewards."
			/>

			<ForgotPasswordDialog
				form={forgotPasswordForm}
				onSubmit={onForgotPasswordSubmit}
				showForgotPasswordDialog={showForgotPasswordDialog}
				setShowForgotPasswordDialog={setShowForgotPasswordDialog}
			/>

			<ResetPasswordDialog
				form={resetPasswordForm}
				onSubmit={onResetPasswordSubmit}
				showResetPasswordDialog={showResetPasswordDialog}
				setShowResetPasswordDialog={setShowResetPasswordDialog}
			/>

			<ActivateAccountDialog
				form={activateAccountForm}
				onSubmit={onActivateAccountSubmit}
				showActivateAccountDialog={showActivateAccountDialog}
				setShowActivateAccountDialog={setShowActivateAccountDialog}
				onResend={onResendVerificationCode}
			/>

			<div className="flex w-full max-w-[480px] flex-col gap-6">
				<Link to={HomeRoute} className="w-fit">
					<LogoIcon height={56} width={53} />
				</Link>

				<div className="flex flex-col gap-2">
					<p className="web-heading-h3 text-white">Welcome back</p>
					<p className="web-body-b1 text-white-80">
						Log in to manage entries and track rewards.
					</p>
				</div>

				<div className="flex flex-col gap-3">
					<GoogleSignInButton
						onAccessToken={onGoogleCredential}
						label="Sign in with Google"
					/>
					<div className="flex items-center gap-3">
						<div className="h-px flex-1 bg-white/30" />
						<span className="web-body-b3 text-white/70">or</span>
						<div className="h-px flex-1 bg-white/30" />
					</div>
				</div>

				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="flex flex-col gap-4"
					>
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-white-80">Email</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											type="email"
											placeholder="Email address"
											className="bg-blue-900-20 text-white placeholder:text-white placeholder:opacity-70"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-white-80">Password</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											type="password"
											placeholder="Password"
											eyeColor="text-white"
											className="bg-blue-900-20 text-white placeholder:text-white placeholder:opacity-70"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="-mt-1 flex justify-end">
							<button
								type="button"
								onClick={() => setShowForgotPasswordDialog(true)}
								className="web-body-b3 text-white/80 underline-offset-4 hover:text-white hover:underline"
							>
								Forgot your password?
							</button>
						</div>

						<div className="mt-2 flex justify-center">
							<Captcha
								onVerify={setCaptchaToken}
								options={{ size: "flexible" }}
							/>
						</div>

						<Button
							type="submit"
							variant="white"
							disabled={!captchaToken || isSubmitting}
						>
							{isSubmitting ? "Logging in…" : "Log in"}
						</Button>

						<span className="web-body-b1 w-full text-center text-white">
							New here?{" "}
							<Link to={SignUpRoute} className="web-button text-white">
								Sign Up
							</Link>
						</span>
					</form>
				</Form>
			</div>
		</div>
	);
}
